import type {
  DetectedHdpeContent,
  DetectedPetContent,
  DetectedUbcContent,
  EvaluatedSpecification,
  EvidenceUploadMode,
  SimpleEvaluatedSpecification,
  SimpleMaterialQualityCategory,
} from 'api';
import { useTranslation } from 'i18n';
import { Box, DataCard, Link, Stack, Typography } from 'ui';
import { Insights } from 'ui/icons';
import {
  buildEvaluatedSpecsItems,
  EvaluatedSpecificationComposition,
} from '../../utils/buildEvaluatedSpecsItems/buildEvaluatedSpecsItems';
import { formatDirtScore } from '../../utils/formatDirtScore/formatDirtScore';
import { getTargetSpecification } from '../../utils/getTargetSpecification/getTargetSpecification';
import { AdminAiVersionTitle } from '../AdminAiVersionTitle/AdminAiVersionTitle';
import { MaterialBreakdownItem } from '../MaterialBreakdownItem/MaterialBreakdownItem';
import { RepresentativeEvidenceSection } from '../PETRepresentativeEvidenceSection/PETRepresentativeEvidenceSection';
import { SpecificationAlerts } from '../SpecificationAlerts/SpecificationAlerts';

type MaterialCategoryContext = Pick<SimpleMaterialQualityCategory, 'id' | 'name'> | null;

export const EvaluatedSpecsBreakdown = ({
  specs,
  composition,
  detectedContent,
  parentId,
  uploadMode,
  showAlerts = true,
  latestRunAIVersion,
  materialCategory,
}: {
  specs: Array<EvaluatedSpecification | SimpleEvaluatedSpecification>;
  composition?: EvaluatedSpecificationComposition;
  detectedContent?: DetectedUbcContent | DetectedPetContent | DetectedHdpeContent;
  parentId?: number;
  uploadMode?: EvidenceUploadMode;
  showAlerts?: boolean;
  latestRunAIVersion?: string;
  materialCategory?: MaterialCategoryContext;
}) => {
  const { language, t } = useTranslation('features');
  const categoryId = materialCategory?.id ?? null;
  const settingsHref = categoryId != null ? `/settings/?categoryId=${categoryId}` : '/settings/';
  const settingsLabel = materialCategory?.name ?? t('breakdowns.evaluatedSpecsBreakdown.thisMaterial');
  const items = buildEvaluatedSpecsItems({ composition, hideZeroOrSmallValues: true, language, specs, t });
  const compositionWithDirtScore = composition != null && 'dirt_score' in composition ? composition : null;

  const targetSpecification = getTargetSpecification(specs) ?? undefined;

  return (
    <DataCard
      dataTestId="evaluated-spec-breakdown"
      slotProps={{
        header: {
          avatar: <Insights />,
          title: (
            <AdminAiVersionTitle title={t('breakdowns.evaluatedSpecsBreakdown.title')} version={latestRunAIVersion} />
          ),
        },
      }}
    >
      <DataCard.Item colSpan={12}>
        <Stack
          direction="column"
          sx={{
            gap: 2,
          }}
        >
          {showAlerts ? <SpecificationAlerts specs={specs} /> : null}

          <div>
            {items.map((item, index) => (
              <Box key={`${item.label}-${index}`}>
                <MaterialBreakdownItem
                  label={item.label}
                  value={item.value}
                  chipText={
                    targetSpecification?.id !== undefined &&
                    item.dataTestId === `evaluated-spec-${targetSpecification.id}`
                      ? t('breakdowns.evaluatedSpecsBreakdown.targetChip')
                      : undefined
                  }
                  variant={item.variant}
                  dataTestId={item.dataTestId}
                  secondaryValue={item.secondaryValue}
                  valueColor={item.valueColor}
                  showValueStatusIcon={item.showValueStatusIcon}
                  indentLevel={item.indentLevel}
                  showUnderline
                />
                {item.isLastInGroup === true && index < items.length - 1 && <Box sx={{ mb: 2 }} />}
              </Box>
            ))}
          </div>

          {compositionWithDirtScore != null ? (
            <MaterialBreakdownItem
              variant="bold"
              label={t('breakdowns.cleanliness.label')}
              tooltipContent={t('breakdowns.cleanliness.tooltip')}
              value={formatDirtScore(compositionWithDirtScore.dirt_score)}
            />
          ) : null}

          <Typography color="secondary" variant="body2">
            {t('breakdowns.evaluatedSpecsBreakdown.customizeTableFor')}
            &nbsp;
            <Link href={settingsHref} target="_blank">
              {settingsLabel}
            </Link>
          </Typography>
          {detectedContent ? (
            <RepresentativeEvidenceSection
              detectedContent={detectedContent}
              parentId={parentId}
              uploadMode={uploadMode}
              targetName={targetSpecification?.name}
              materialCategory={materialCategory}
            />
          ) : null}
        </Stack>
      </DataCard.Item>
    </DataCard>
  );
};
